import type { SchemaMap } from 'joi';
import type { SchemaOf } from '../helpers';
import { Joi, objectSchemaOf } from '../helpers';

import {
  Conversation,
  Dialogue,
  Presence,
  Recording,
  Reports,
  UserInput,
  View,
  Vocalise,
  Vision,
  FeatureFlags,
  VoiceToText,
  Flows,
  System,
  Microphone,
} from './features';

export type RevisionGlobal = number;
export type RevisionUser = [number, number?];
export type Revision = RevisionGlobal | RevisionUser;

export const revisionGlobalSchema = Joi.number().required();
export const revisionUserSchema = Joi.array()
  .items(Joi.number().required())
  .min(1)
  .max(2);
export const revisionSchema = Joi.alternatives(
  revisionGlobalSchema,
  revisionUserSchema
);

export function isEqualRevision(a: Revision, b: Revision): boolean {
  if (typeof a === typeof b) {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a[0] === b[0] && a[1] === b[1];
    } else {
      return a === b;
    }
  }

  return false;
}

export interface IBaseConfig {
  conversation: Conversation.IConfig;
  dialogue: Dialogue.IConfig;
  presence: Presence.IConfig;
  recording: Recording.IConfig;
  reports: Reports.IConfig;
  userInput: UserInput.IConfig;
  view: View.IConfig;
  vocalise: Vocalise.IConfig;
  vision: Vision.IConfig;
  voiceToText: VoiceToText.IConfig;
  featureFlags: FeatureFlags.IConfig;
  flows: Flows.IConfig;
  system: System.IConfig;
  microphone: Microphone.IConfig;
}

export type DeepPartial<T> = { [P in keyof T]?: DeepPartial<T[P]> };

export type IDeepPartialBaseConfig = DeepPartial<IBaseConfig>;

// config stored in the database as a record, not merged with anything
export type IRawConfig = IDeepPartialBaseConfig & {
  revision: RevisionGlobal;
};

// config merged with default config, to be used by apps
export interface IConfig extends IBaseConfig {
  revision: Revision;
}

// config returned by the api to be merged with default config
export interface IPartialConfig extends IDeepPartialBaseConfig {
  revision: Revision;
}

const objectUnknown = <T>(schema: Joi.SchemaMap<T>) =>
  Joi.object<T>(schema).unknown(true);

const objectUnknownOptional = <T>(schema: SchemaMap<T>) =>
  objectUnknown<T>(schema).optional();

export const partialBaseConfigSchemaMap: SchemaOf<IDeepPartialBaseConfig> = {
  conversation: objectUnknownOptional(Conversation.schemaMap),
  dialogue: objectUnknownOptional(Dialogue.schemaMap),
  presence: objectUnknownOptional(Presence.schemaMap),
  recording: objectUnknownOptional(Recording.schemaMap),
  reports: objectUnknownOptional(Reports.schemaMap),
  userInput: objectUnknownOptional(UserInput.schemaMap),
  view: objectUnknownOptional(View.schemaMap),
  vocalise: objectUnknownOptional(Vocalise.schemaMap),
  vision: objectUnknownOptional(Vision.schemaMap),
  voiceToText: objectUnknownOptional(VoiceToText.schemaMap),
  featureFlags: FeatureFlags.schemaMap.optional(),
  system: objectUnknownOptional(System.schemaMap),
  microphone: objectUnknownOptional(Microphone.schemaMap),
  flows: objectUnknownOptional(Flows.schemaMap),
};

export const baseConfigSchemaMap: SchemaOf<IBaseConfig> = {
  conversation: Joi.object(Conversation.schemaMapDefault).required(),
  dialogue: Joi.object(Dialogue.schemaMapDefault).required(),
  presence: Joi.object(Presence.schemaMapDefault).required(),
  recording: Joi.object(Recording.schemaMapDefault).required(),
  reports: Joi.object(Reports.schemaMapDefault).required(),
  userInput: Joi.object(UserInput.schemaMapDefault).required(),
  view: Joi.object(View.schemaMapDefault).required(),
  vocalise: Joi.object(Vocalise.schemaMapDefault).required(),
  vision: Joi.object(Vision.schemaMapDefault).required(),
  voiceToText: Joi.object(VoiceToText.schemaMapDefault).required(),
  featureFlags: FeatureFlags.schemaMapDefault.optional().required(),
  system: Joi.object(System.schemaMapDefault).required(),
  microphone: Joi.object(Microphone.schemaMapDefault).required(),
  flows: Joi.object(Flows.schemaMapDefault).required(),
};

export const rawConfigSchema = objectSchemaOf<IRawConfig>({
  revision: revisionGlobalSchema,
  ...partialBaseConfigSchemaMap,
});

export const partialConfigSchema = objectUnknown<IPartialConfig>({
  revision: revisionSchema,
  ...partialBaseConfigSchemaMap,
});

export const baseConfigSchema = objectSchemaOf<IBaseConfig>(
  partialBaseConfigSchemaMap
);

export const fullConfigSchema = objectSchemaOf<IConfig>({
  revision: revisionSchema.required(),
  ...baseConfigSchemaMap,
});
